import { z } from 'zod/v3';

/**
 * RFC-179 canonical schema: FIREWALL_RULE.
 *
 * Covers cross-cloud "this rule allows traffic from X to Y" semantics
 * shared by Azure NSG rules, AWS Security Group rules, AWS NACL entries,
 * and GCP firewall rules. Provider-specific quirks (Azure rule priority,
 * AWS no-deny on SG, GCP service-account targeting) are intentionally
 * out of the canonical schema and accessible via rawNative when a check
 * needs them.
 */

export const FIREWALL_RULE_SCHEMA_VERSION = 1;

/** Port can be a single number, a range, or any. */
const PortSpec = z.union([
  z.number().int().min(0).max(65535),
  z.object({
    from: z.number().int().min(0).max(65535),
    to: z.number().int().min(0).max(65535),
  }),
  z.literal('any'),
]);

export const FirewallRule = z.object({
  /** Inbound or outbound. */
  direction: z.enum(['inbound', 'outbound']),

  /** What the rule does. AWS SG has no deny — projection sets 'allow' + canonicalLossy=true. */
  action: z.enum(['allow', 'deny']),

  /** Tcp/Udp/Icmp/any. 'any' canonicalizes Azure's '*' and AWS's '-1'. */
  protocol: z.enum(['tcp', 'udp', 'icmp', 'any']),

  /** Source CIDRs. Empty when source is tag-based (security group / service tag); see hasPriority/isStateful flags. */
  sourceCidrs: z.array(z.string()),
  /** Destination CIDRs. Empty when destination is tag-based. */
  destinationCidrs: z.array(z.string()),

  /** Destination ports — numbers, ranges, or 'any'. */
  destinationPorts: z.array(PortSpec),

  /**
   * Provider-quirk flags so cross-cloud predicates can stay honest without
   * lying about provider behavior.
   */
  /** False for AWS SG (additive permit, no priority/order). */
  hasPriority: z.boolean(),
  /** False for AWS NACL (stateless). */
  isStateful: z.boolean(),
});

export type FirewallRule = z.infer<typeof FirewallRule>;
